// Small shared pieces every OT screen reaches for.

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { IPD_COLORS, IpdButton, IpdDetailRow, IpdTextField } from "../../ipd/utils";
import {
  COUNT_RESULTS,
  LIFECYCLE_LABELS,
  trackingTone,
  TRACKING_LABELS,
  firmnessStyle,
} from "../constants";

/**
 * Show the result of a mutation.
 *
 * The OT API answers a business refusal with HTTP 200, status "Failed" and
 * is_valid TRUE — deliberately, because an is_valid:false body trips the global
 * axios interceptor and logs the user out (see api/otApi.js). So a refusal is a
 * resolved promise with ok === false, and this is the one place that turns it
 * into a toast. Returns `ok`, so call sites read:
 *
 *     if (!report(await bookTheatre(...))) return;
 */
export function report(envelope, successMessage) {
  if (!envelope) return false;
  if (envelope.ok) {
    toast.success(successMessage || envelope.message || "Saved");
    // A successful call can still carry advisory warnings — a patient booked
    // twice, a room past its ceiling. They are not failures and must not be
    // shown as such, but swallowing them would defeat the point of soft guards.
    (envelope.data?.warnings || envelope.warnings || []).forEach((w) => toast.warn(w));
    advisories(envelope).forEach((w) => toast.warn(w));
    return true;
  }
  toast.error(envelope.message || "That did not work");
  return false;
}

/** The named advisories a successful OT call can carry.
 *
 *  Each one reports something that DID NOT HAPPEN alongside something that
 *  did: the charge that was not captured, the bed that did not move, the pain
 *  nobody has treated. They are not failures — the action stands — but a
 *  warning nothing displays is the same as no warning at all, which is how
 *  beds ended up held for patients who were never coming back.
 */
function advisories(envelope) {
  return [
    envelope.billing_warning,
    envelope.bed_warning || envelope.data?.bed_warning,
    envelope.pain_warning,
  ].filter(Boolean);
}

/** Turn a thrown transport error into a message worth showing. */
export function errorMessage(err, fallback = "Something went wrong") {
  return err?.body?.message || err?.message || fallback;
}

// Chip metrics match the Emergency badges (height 22, 11px, a real border)
// so a case reads the same wherever it appears in the product.
const CHIP_SX = { height: 22, fontSize: 11, fontWeight: 700 };

/** The firmness badge (OT_0022). The value is always the server's `firmness`. */
export function FirmnessChip({ firmness, size = "small" }) {
  const tone = firmnessStyle(firmness);
  return (
    <Chip
      size={size}
      label={tone.label}
      sx={{
        ...CHIP_SX,
        backgroundColor: tone.bg,
        color: tone.text,
        border: `1px solid ${tone.border}`,
      }}
    />
  );
}

export function LifecycleChip({ status, size = "small" }) {
  return (
    <Chip
      size={size}
      variant="outlined"
      label={LIFECYCLE_LABELS[status] || status || "—"}
      sx={{ fontWeight: 600, fontSize: 11.5, borderColor: IPD_COLORS.border }}
    />
  );
}

/** Where the patient physically is. Null until surgery day (ADR-0003). */
export function TrackingChip({ status, size = "small" }) {
  if (!status) {
    return (
      <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
        Not started
      </Typography>
    );
  }
  const tone = trackingTone(status);
  return (
    <Chip
      size={size}
      label={TRACKING_LABELS[status] || status}
      sx={{
        ...CHIP_SX,
        backgroundColor: tone.bg,
        color: tone.text,
        border: `1px solid ${tone.border}`,
      }}
    />
  );
}

export function CountResultChip({ result }) {
  const tone = COUNT_RESULTS[result] || COUNT_RESULTS.PENDING;
  return (
    <Chip
      size="small"
      label={tone.label}
      sx={{
        ...CHIP_SX,
        color: tone.text,
        backgroundColor: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    />
  );
}

/**
 * A labelled read-only value.
 *
 * Built on the shared `IpdDetailRow` rather than a private label/value pair, so
 * an OT detail panel reads identically to an Emergency or IPD one — same label
 * column, same type scale, same em-dash for an empty value.
 */
export function Field({ label, value, span = 1 }) {
  return (
    <Box sx={{ gridColumn: `span ${span}`, minWidth: 0 }}>
      <IpdDetailRow label={label} value={value} />
    </Box>
  );
}

/**
 * A responsive grid of Fields.
 *
 * `auto-fit` with a 260px minimum rather than fixed breakpoint columns: it keeps
 * every cell wide enough for IpdDetailRow's 130px label column at any width,
 * which fixed columns do not. `columns` is accepted as an upper bound so a
 * two-fact card does not stretch across five.
 */
export function FieldGrid({ columns = 4, children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: `repeat(auto-fit, minmax(260px, 1fr))`,
        },
        maxWidth: columns <= 2 ? 720 : "none",
        columnGap: 3,
        rowGap: 0,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * A blocking notice, used wherever the server has refused to let a case
 * proceed. The blockers come from the server's gate so the screen and the
 * endpoint can never disagree about why.
 */
export function GateNotice({ gate, readyMessage = "Ready to proceed" }) {
  if (!gate) return null;
  if (gate.satisfied) {
    return (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: IPD_COLORS.success.bg,
          color: IPD_COLORS.success.text,
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {readyMessage}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        backgroundColor: IPD_COLORS.warning.bg,
        color: IPD_COLORS.warning.text,
        fontSize: 13,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>
        Not ready yet
      </Typography>
      {(gate.blockers || []).map((b, i) => (
        <Box key={i} component="div" sx={{ mb: 0.25 }}>
          • {b}
        </Box>
      ))}
    </Box>
  );
}

/**
 * A select over a list of options.
 *
 * MUI's `TextField select` renders MenuItem children, not `<option>`, and every
 * OT form needs the same {value, label} mapping — so it lives here once rather
 * than as fifteen near-identical `.map(o => <MenuItem/>)` blocks.
 *
 * `options` accepts either `{value, label}` objects or bare strings.
 */
export function OtSelect({ options = [], placeholder, value, onChange, ...rest }) {
  const normalised = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );
  return (
    <IpdTextField
      select
      fullWidth
      value={value ?? ""}
      onChange={onChange}
      {...rest}
    >
      {placeholder && (
        <MenuItem value="">
          <em>{placeholder}</em>
        </MenuItem>
      )}
      {normalised.map((o) => (
        <MenuItem key={String(o.value)} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </IpdTextField>
  );
}


/**
 * Report a mutation that the user did not explicitly ask to "save".
 *
 * Silent on success. A checklist is ticked at the speed someone reads it, and
 * eleven "Checklist saved" toasts in eight seconds is not eleven pieces of
 * information — it is a wall of green that trains people to dismiss toasts
 * without reading them, including the one that mattered.
 *
 * Failures and warnings still speak, because those are the ones worth reading.
 */
export function reportQuiet(envelope) {
  if (!envelope) return false;
  if (envelope.ok) {
    (envelope.data?.warnings || envelope.warnings || []).forEach((w) => toast.warn(w));
    advisories(envelope).forEach((w) => toast.warn(w));
    return true;
  }
  toast.error(envelope.message || "That did not work");
  return false;
}

/**
 * A checkbox that saves itself, and moves the moment it is clicked.
 *
 * It shows the click straight away and only falls back to the server's value if
 * the save is refused. The alternative — wait for the round trip, then re-fetch
 * the panel, then re-render — is what made a checklist feel like it was fighting
 * back: each tick froze every other box on the card for as long as the network
 * took, so a nurse working down eleven items paid that cost eleven times.
 *
 * `onSave` returns truthy when the server accepted it. Anything else reverts.
 */
export function SaveCheckbox({ checked, label, hint, strong, disabled, onSave }) {
  const server = Boolean(checked);
  const [pending, setPending] = useState(null);
  const shown = pending === null ? server : pending;

  // The server has caught up, or overruled us. Either way, stop overriding it.
  useEffect(() => {
    setPending(null);
  }, [server]);

  return (
    <FormControlLabel
      sx={{
        m: 0,
        py: 0.4,
        pr: 1,
        borderRadius: 1,
        alignItems: "flex-start",
        transition: "background-color 120ms",
        "&:hover": { backgroundColor: IPD_COLORS.pageBg },
      }}
      control={
        <Checkbox
          size="small"
          checked={shown}
          disabled={disabled}
          sx={{ py: 0.25 }}
          onChange={async (event) => {
            const next = event.target.checked;
            setPending(next);
            await onSave(next);
            // Cleared either way. If the save went through, the parent has
            // already been patched and the effect above cleared this — so
            // this is a no-op. If it was refused, or accepted without
            // changing the value, the server's answer is the one to show and
            // nothing else would ever put it back.
            setPending(null);
          }}
        />
      }
      label={
        <Box sx={{ pt: 0.35 }}>
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.35,
              fontWeight: strong ? 600 : 400,
              color: shown ? IPD_COLORS.textPrimary : IPD_COLORS.textSecondary,
            }}
          >
            {label}
          </Typography>
          {hint && (
            <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>{hint}</Typography>
          )}
        </Box>
      }
    />
  );
}

/**
 * A checkbox that changes a DRAFT, and saves nothing by itself.
 *
 * SaveCheckbox writes on every click. That is right for a lone flag and wrong
 * for a checklist: a WHO Sign-In is eleven boxes and a ward pre-op list is
 * fifteen, so working down one fired fifteen requests, fifteen server round
 * trips and fifteen re-renders of the card. The person ticking is not making
 * fifteen decisions -- they are making one pass and then saying "done".
 *
 * So the ticks collect in the parent and the card writes them together. The
 * cost of that is a window where the screen and the server disagree, which is
 * why every card using this must show that something is unsaved -- DraftBar.
 */
export function DraftCheckbox({ checked, label, hint, strong, disabled, onChange }) {
  return (
    <FormControlLabel
      sx={{
        m: 0,
        py: 0.4,
        pr: 1,
        borderRadius: 1,
        alignItems: "flex-start",
        transition: "background-color 120ms",
        "&:hover": { backgroundColor: IPD_COLORS.pageBg },
      }}
      control={
        <Checkbox
          size="small"
          checked={Boolean(checked)}
          disabled={disabled}
          sx={{ py: 0.25 }}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={
        <Box sx={{ pt: 0.35 }}>
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.35,
              fontWeight: strong ? 600 : 400,
              color: checked ? IPD_COLORS.textPrimary : IPD_COLORS.textSecondary,
            }}
          >
            {label}
          </Typography>
          {hint && (
            <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>{hint}</Typography>
          )}
        </Box>
      }
    />
  );
}

/**
 * The bar a batched checklist is saved from. Nothing when nothing is pending.
 *
 * Sticky rather than simply placed at the end of the list, because the failure
 * this whole pattern risks is a checklist someone ticked and walked away from,
 * and the fix for that is that the way to commit it is never scrolled off.
 */
export function DraftBar({ count, busy, onSave, onDiscard }) {
  if (!count) return null;
  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        mt: 1.5,
        px: 1.5,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
        borderRadius: 1,
        backgroundColor: IPD_COLORS.warning.bg,
        border: `1px solid ${IPD_COLORS.warning.border}`,
      }}
    >
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, flex: 1, color: IPD_COLORS.warning.text }}>
        {count} {count === 1 ? "tick is" : "ticks are"} not saved yet
      </Typography>
      <Button size="small" onClick={onDiscard} disabled={busy} sx={{ fontSize: 12.5 }}>
        Discard
      </Button>
      <IpdButton size="small" onClick={onSave} disabled={busy}>
        {busy ? "Saving..." : "Save checklist"}
      </IpdButton>
    </Box>
  );
}

/**
 * A text input that saves when you leave it, and only if you changed it.
 *
 * The `defaultValue` + `onBlur` pattern it replaces fired a save every time the
 * field lost focus, including when someone tabbed straight through it, so
 * clicking across a form wrote every field back unchanged.
 */
export function SaveTextField({ value, onSave, ...rest }) {
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  return (
    <IpdTextField
      fullWidth
      {...rest}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (draft !== (value ?? "")) onSave(draft);
      }}
    />
  );
}

/**
 * The container a set of SaveCheckboxes sits in, with its progress said out
 * loud.
 *
 * "7 of 11" is the one thing someone working down a checklist wants to know and
 * the one thing a column of ticks does not tell them at a glance.
 */
export function CheckList({ title, done, total, blocking, children }) {
  const complete = total > 0 && done === total;
  return (
    <Box>
      {(title || total > 0) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 1,
            mb: 0.75,
          }}
        >
          {title && (
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.02em" }}>
              {title}
            </Typography>
          )}
          {total > 0 && (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: complete ? IPD_COLORS.success.text : IPD_COLORS.textSecondary,
              }}
            >
              {done} of {total}
            </Typography>
          )}
          {blocking && !complete && (
            <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.warning.text }}>{blocking}</Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
          columnGap: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
